/**
 * Languages Section - Kiosk Settings
 * Default Language dropdown and Supported Languages multi-select (matching Flutter UI)
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronDown, ChevronRight, Search, X } from 'lucide-react';
import { useKioskSettingsStore } from '../../../stores/kiosk-settings-store';
import { parseCompoundLanguageCode } from '@/features/translations/utils/language-code';
import { Label } from '@/core/components/ui/label';
import { Input } from '@/core/components/ui/input';
import { Checkbox } from '@/core/components/ui/checkbox'; // Used in language selector dropdown
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/core/components/ui/select';
import {
  Popover,
  PopoverContent,
  PopoverTrigger
} from '@/core/components/ui/popover';

interface LanguagesSectionProps {
  isOpen: boolean;
  onToggle: () => void;
}

export const LanguagesSection = ({ isOpen, onToggle }: LanguagesSectionProps) => {
  const { t } = useTranslation('assignEv');
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const { settings, updateField, supportedLanguageOptions } = useKioskSettingsStore();

  // Languages are fetched during fetchSettings in KioskSettingsTab
  // No need to fetch separately here

  if (!settings) return null;

  const currentLanguage = settings.language || '';
  const supportedLanguages = settings.supportedLanguages || [];

  // Filter languages for search
  const filteredLanguages = supportedLanguageOptions.filter(
    (lang) =>
      lang.languageName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      lang.languageCode.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleToggleLanguage = (code: string) => {
    const newLanguages = supportedLanguages.includes(code)
      ? supportedLanguages.filter((l) => l !== code)
      : [...supportedLanguages, code];
    updateField('supportedLanguages', newLanguages);
  };

  const handleDefaultLanguageChange = (code: string) => {
    updateField('language', code);
  };

  // "Hebrew" or "Hebrew (paz)" when languageCode is a compound "he#paz" code
  const getLanguageDisplayName = (lang: { languageCode: string; languageName: string }) => {
    const { operatorName } = parseCompoundLanguageCode(lang.languageCode);
    return operatorName ? `${lang.languageName} (${operatorName})` : lang.languageName;
  };

  // Display selected languages as comma-separated names (max 3, then "...")
  const getDisplayText = () => {
    if (supportedLanguages.length === 0) {
      return 'Select supported languages';
    }
    const names = supportedLanguages
      .map((code) => {
        const lang = supportedLanguageOptions.find(l => l.languageCode === code);
        return lang ? getLanguageDisplayName(lang) : code;
      })
      .slice(0, 2);
    const suffix = supportedLanguages.length > 2 ? '...' : '';
    return `${names.join(', ')}${suffix}`;
  };

  return (
    <div className="rounded-lg overflow-hidden shadow-sm hover:shadow-md transition-shadow">
      {/* Section Header */}
      <button
        onClick={onToggle}
        className="w-full px-4 py-3 flex items-center justify-between bg-gray-100 hover:bg-gray-200 transition-colors"
      >
        <div className="flex items-center gap-3">
          {isOpen ? (
            <ChevronDown className="w-5 h-5 text-gray-700" />
          ) : (
            <ChevronRight className="w-5 h-5 text-gray-700" />
          )}
          <h3 className="font-bold text-gray-900 text-base">{t('kiosk.languagesSection.title')}</h3>
        </div>
      </button>

      {/* Section Content */}
      {isOpen && (
        <div className="px-4 py-3 bg-white">
          {/* Languages Row - Horizontal Layout */}
          <div className="flex gap-3 items-end flex-nowrap">
            {/* Default Language - Left Side */}
            <div className="w-32 shrink-0">
              <Label className="text-xs font-medium text-gray-600 mb-2 block">
                {t('kiosk.languagesSection.fields.defaultLanguage')}
              </Label>
              <Select value={currentLanguage} onValueChange={handleDefaultLanguageChange}>
                <SelectTrigger className="bg-white border-gray-300">
                  <SelectValue placeholder={t('kiosk.languagesSection.fields.defaultLanguagePlaceholder')} />
                </SelectTrigger>
                <SelectContent
                  avoidCollisions={false}
                  className="max-h-[min(24rem,var(--radix-select-content-available-height,24rem))]"
                >
                  {supportedLanguageOptions.map((lang) => (
                    <SelectItem key={lang.languageCode} value={lang.languageCode}>
                      {getLanguageDisplayName(lang)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Supported Languages - Right Side (Multi-Select Dropdown) */}
            <div className="w-52 shrink-0">
              <Label className="text-xs font-medium text-gray-600 mb-2 block">
                {t('kiosk.languagesSection.fields.supportedLanguages')}
              </Label>

              <Popover open={isDropdownOpen} onOpenChange={setIsDropdownOpen}>
                <PopoverTrigger asChild>
                  <button className="w-full px-3 py-2 bg-white border border-gray-300 rounded-md text-sm text-left flex items-center justify-between hover:bg-gray-50 transition-colors overflow-hidden">
                    <span className={`truncate ${supportedLanguages.length > 0 ? 'text-gray-900' : 'text-gray-500'}`}>
                      {getDisplayText()}
                    </span>
                    <ChevronDown className="w-4 h-4 text-gray-400" />
                  </button>
                </PopoverTrigger>

                <PopoverContent className="w-80 p-0" align="start" avoidCollisions={false}>
                  <div className="space-y-2 p-4">
                    {/* Search Input */}
                    <div className="relative">
                      <Search className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
                      <Input
                        placeholder={t('kiosk.languagesSection.fields.searchPlaceholder')}
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="pl-9 border-gray-300"
                      />
                      {searchTerm && (
                        <button
                          onClick={() => setSearchTerm('')}
                          className="absolute right-3 top-2.5 text-gray-400 hover:text-gray-600"
                        >
                          <X className="h-4 w-4" />
                        </button>
                      )}
                    </div>

                    {/* Select All / Clear */}
                    <div className="flex justify-between border-b border-gray-100 pb-2">
                      <button
                        onClick={() => updateField('supportedLanguages', supportedLanguageOptions.map(l => l.languageCode))}
                        className="text-xs font-medium text-blue-600 hover:text-blue-800"
                      >
                        Select All
                      </button>
                      <button
                        onClick={() => updateField('supportedLanguages', [])}
                        className="text-xs font-medium text-gray-500 hover:text-gray-700"
                      >
                        Clear
                      </button>
                    </div>

                    {/* Languages List with Checkboxes */}
                    <div className="max-h-64 overflow-y-auto space-y-1">
                      {filteredLanguages.length > 0 ? (
                        filteredLanguages.map((lang) => (
                          <button
                            key={lang.languageCode}
                            onClick={() => handleToggleLanguage(lang.languageCode)}
                            className="w-full flex items-center gap-3 px-3 py-2 rounded-md hover:bg-gray-100 transition-colors text-left"
                          >
                            <Checkbox
                              checked={supportedLanguages.includes(lang.languageCode)}
                              onCheckedChange={() => handleToggleLanguage(lang.languageCode)}
                            />
                            <span className="text-sm">{getLanguageDisplayName(lang)}</span>
                          </button>
                        ))
                      ) : (
                        <div className="px-3 py-2 text-sm text-gray-500 text-center">
                          {t('kiosk.languagesSection.fields.noLanguagesFound')}
                        </div>
                      )}
                    </div>
                  </div>
                </PopoverContent>
              </Popover>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
